import { Card, Col, Image, Layout, Row, Spin } from "antd";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useParams } from "react-router";
import { Logger } from '../..//utilites/loglySetup';
import { AuthApi } from "../../apis/auth-api";
import GovendaLogo from '../../assets/govendaAdminLogo';
import FooterLinks from "../../components/FooterLinks";
import GovendaCSLogo from '../../assets/GovendaCSLogo.png';
import { cancel } from "../../utilites/api";
import { AuthContext } from "../../utilites/auth";
import BgAnimationCanvas from "../login/BackgroundAnimation";
import PasswordResetCompleteCard from "./components/PasswordResetCompleteCard";
import PasswordResetExpiredCard from "./components/PasswordResetExpiredCard";
import PasswordResetForm from "./forms/PasswordResetForm";
import "./PasswordReset.scss";
import CloseableErrorMessage from "../../components/CloseableErrorMessage";

type RouteParams = {
    token: string
}

function PasswordResetPage() {
    const { profile, setLoggedIn } = useContext(AuthContext);
    /* eslint-disable no-unused-vars */
    const [cookies, setCookie, removeCookie] = useCookies(['sessionUserName']);
    const [key, setKey] = useState<string>('form');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [useToken, setUseToken] = useState<string>();
    const location = useLocation();

    let { token } = useParams<RouteParams>();

    useEffect(() => {
        document.title = `Govenda - Reset Password`;
        if(!!localStorage.getItem('currentUser')) {
          logout();
        }
        checkTokenStatus();
    }, [])

    useEffect(() => {
        return () => {
            cancel();
        }
    }, [])

    /**
     * Retrieve userId of reset token
     * If userId is 0 or less, display PasswordResetExpiredCard
     * @returns 
     */
    const checkTokenStatus = async () => {

        var tkn = token
        if(!token) {
            let params = new URLSearchParams(location.search)
            if(params.get("strToken")) {
                setUseToken(params.get("strToken"))
                tkn = params.get("strToken")
            }
        }

        try {
            setIsLoading(true);
            const userId = await AuthApi.getUserIdFromResetToken(tkn);

            if (userId <= 0) {
                setKey("expired")
            }

            return true;
        } catch (e) {
            if (axios.isCancel(e))
                Logger().log(e);
            else
                Logger().error(e);

            return false;
        } finally {
            setIsLoading(false);
        }
    }

    /**
     * Reset user password
     * @param newPassword 
     * @returns 
     */
    const resetPassword = async (newPassword: string) => {
        try {
            setIsLoading(true);
            await AuthApi.postResetPassword({ token: useToken, newPassword, message: "Password reset successful" });

            return { success: true };
        } catch (e) {
            if (axios.isCancel(e))
                Logger().log(e);
            else
                Logger().error(e);

            return { success: false, error: e?.response?.data?.Message };
        } finally {
            setIsLoading(false);
        }
    }

    /**
     * Sign out logged in user
     */
    const logout = async () => {
        try {
            await AuthApi.signOut();
            setLoggedIn(false);
            removeCookie('sessionUserName', { path: "/" });
            setCookie('forcedSignOut', true, { path: "/" });
            localStorage.removeItem('currentUser');
            localStorage.removeItem('downloadFileId');
        }
        catch (error) {
            if (axios.isCancel(error)) 
              Logger().log(error);
            else 
              Logger().error(error);
            //If signout is unauthorized, then they were already singed out to begin with
            if(error?.response?.status != 401) {
              CloseableErrorMessage("Error signing out.");
            }
        }          
    }

    const contentList = {
        form: <PasswordResetForm checkTokenStatus={checkTokenStatus} resetPassword={resetPassword} setKey={setKey} />,
        success: <PasswordResetCompleteCard />,
        expired: <PasswordResetExpiredCard />
    };

    const titleList = {
        form: "Reset Password",
        success: "Reset Password Successful",
        expired: "Reset Password Token Expired"
    };

    return (
        <Layout className="login-layout">
            <BgAnimationCanvas id="BgAnimationCanvas" />
            <Card
                id="passwordResetCard"
                activeTabKey={key}
                className={key}
            >
                <Row justify="center">
                    <Col>
                      <Image src={GovendaCSLogo} preview={false} />
                    </Col>
                </Row>
                <Row justify="center">
                    <Col>
                        <h2>{titleList[key]}</h2>
                    </Col>
                </Row>
                <Spin spinning={isLoading} size="large">
                    {contentList[key]}
                </Spin>
                <FooterLinks />
            </Card>
        </Layout>
    );
}

export default PasswordResetPage;
